import { useState } from "react";
import { useNossaHistoria, TeamMember } from "@/hooks/useNossaHistoria";
import { nossaHistoriaApi } from "@/integrations/mysql/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Trash2, Edit2, Plus, Save, X, GripVertical, Eye, EyeOff, UserCircle } from "lucide-react";
import { PhotoCropUpload } from "@/components/ui/PhotoCropUpload";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableMember({ member, onEdit, onDelete, onToggle }: {
  member: TeamMember;
  onEdit: (m: TeamMember) => void;
  onDelete: (id: string) => void;
  onToggle: (m: TeamMember) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: member.id });
  return (
    <Card ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 shrink-0">
            <GripVertical className="w-5 h-5" />
          </div>
          {member.photo_url ? (
            <img src={member.photo_url} alt={member.name} className="w-10 h-10 rounded-xl object-cover shrink-0" onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0"><UserCircle className="w-6 h-6 text-gray-400" /></div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-sm text-gray-800 truncate">{member.name}</h4>
              {!member.is_active && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded shrink-0">Inativo</span>}
            </div>
            <p className="text-xs text-brand-primary">{member.position}</p>
            <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{member.description}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button onClick={() => onToggle(member)} className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${member.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
              {member.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              {member.is_active ? "Visível" : "Oculto"}
            </button>
            <Button size="sm" variant="outline" onClick={() => onEdit(member)} className="h-7 w-7 p-0"><Edit2 className="w-3.5 h-3.5" /></Button>
            <Button size="sm" variant="outline" onClick={() => { if (confirm('Excluir este membro?')) onDelete(member.id); }} className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:border-red-300"><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function TeamManager() {
  const { teamMembers, teamLoading, updateTeamMember, createTeamMember, deleteTeamMember } = useNossaHistoria();
  const [localItems, setLocalItems] = useState<TeamMember[] | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<Partial<TeamMember>>({ name: '', position: '', description: '', photo_url: '', is_active: true, display_order: 0 });
  const { toast } = useToast();

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const displayed = localItems ?? (teamMembers || []);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const reordered = arrayMove(displayed, displayed.findIndex(m => m.id === active.id), displayed.findIndex(m => m.id === over.id));
    setLocalItems(reordered);
    try {
      await Promise.all(reordered.map((m, i) => nossaHistoriaApi.updateTeam(m.id, { display_order: i })));
      toast({ title: "Ordem atualizada!" });
    } catch {
      toast({ title: "Erro ao atualizar ordem", variant: "destructive" });
      setLocalItems(null);
    }
  };

  const handleCreate = () => { setIsCreating(true); setEditingId(null); setFormData({ name: '', position: '', description: '', photo_url: '', is_active: true, display_order: displayed.length }); };
  const handleEdit = (m: TeamMember) => { setEditingId(m.id); setFormData(m); setIsCreating(false); };
  const handleSave = () => {
    if (!formData.name || !formData.position || !formData.description) return;
    if (isCreating) { createTeamMember(formData as Omit<TeamMember, 'id'>); setIsCreating(false); }
    else if (editingId) { updateTeamMember({ id: editingId, updates: formData }); setEditingId(null); }
    setFormData({ name: '', position: '', description: '', photo_url: '', is_active: true, display_order: 0 });
    setLocalItems(null);
  };
  const handleCancel = () => { setEditingId(null); setIsCreating(false); setFormData({ name: '', position: '', description: '', photo_url: '', is_active: true, display_order: 0 }); };

  if (teamLoading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={handleCreate} disabled={isCreating || !!editingId}><Plus className="w-4 h-4 mr-2" />Adicionar Membro</Button>
      </div>

      {(isCreating || editingId) && (
        <Card className="border-2 border-brand-primary/30">
          <CardHeader className="pb-3"><CardTitle className="text-base">{isCreating ? 'Novo Membro' : 'Editar Membro'}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Nome ({formData.name?.length || 0}/100)</Label>
                <Input className="mt-1" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} maxLength={100} placeholder="Ex: Dra. Brenda Ramos" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Cargo ({formData.position?.length || 0}/200)</Label>
                <Input className="mt-1" value={formData.position} onChange={e => setFormData(p => ({ ...p, position: e.target.value }))} maxLength={200} placeholder="Ex: Nutricionista - CRN 3/63492" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Descrição ({formData.description?.length || 0}/500)</Label>
              <Textarea className="mt-1" value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} maxLength={500} rows={3} />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600 block mb-2">Foto</Label>
              <PhotoCropUpload
                value={formData.photo_url || ""}
                onChange={(url) => setFormData(p => ({ ...p, photo_url: url }))}
                shape="circle"
                aspect={1}
                label="Foto do membro"
                previewClassName="w-20 h-20 rounded-full"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!formData.is_active} onCheckedChange={v => setFormData(p => ({ ...p, is_active: v }))} />
              <Label>Visível no site</Label>
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={handleSave} disabled={!formData.name || !formData.position || !formData.description}><Save className="w-4 h-4 mr-2" />Salvar</Button>
              <Button onClick={handleCancel} variant="outline"><X className="w-4 h-4 mr-2" />Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {displayed.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm">Nenhum membro cadastrado.</div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-400 flex items-center gap-1.5"><GripVertical className="w-3.5 h-3.5" />Arraste para reordenar</p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={displayed.map(m => m.id)} strategy={verticalListSortingStrategy}>
              {displayed.map(m => (
                <SortableMember key={m.id} member={m} onEdit={handleEdit}
                  onDelete={id => { deleteTeamMember(id); setLocalItems(null); }}
                  onToggle={m => updateTeamMember({ id: m.id, updates: { is_active: !m.is_active } })} />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}
